import { createFileRoute } from '@tanstack/react-router';

import {
  WorkflowGraphPage,
  graphSelectionSearch,
  workflowDetailSearchSchema,
} from '@/features/workflows';
import { prefetchWorkspace } from '@/features/workspaces';

/**
 * The workflow's full diagram: the read-only graph, its detail panel, and the
 * keyboard traversal that walks it.
 *
 * **Kept as a page, not turned into a redirect.** Spec 094 made the board the
 * default view of a published workflow, and an earlier pass on that branch read
 * "default" as "only" and forwarded this address to the board. That was wrong in
 * a way nothing in the specification asked for: this page is the sole surface
 * with focusable nodes, ← / → traversal along transitions, and a step's detail —
 * so redirecting it did not relocate a view, it deleted a keyboard-accessible one.
 *
 * Nothing *routes* a reader here by default any more — the sidebar, the workflow
 * list and the palette all open the board (FR-007) — and the board carries an
 * inline diagram for a glance. This is where that glance leads when someone wants
 * to inspect rather than look.
 */
export const Route = createFileRoute(
  '/workspaces/$workspaceId/workflows/$workflowKey/',
)({
  // The selected step is shareable and survives a reload, so it lives in the
  // address rather than in a store.
  validateSearch: workflowDetailSearchSchema,
  // Warm the aggregate the graph reads from, so arriving here does not
  // waterfall into a spinner. Failures are the component's to render, not the
  // loader's to throw.
  loader: ({ context, params }) =>
    prefetchWorkspace(context.queryClient, params.workspaceId),
  component: WorkflowDetailRoute,
});

export function WorkflowDetailRoute() {
  const { workspaceId, workflowKey } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <WorkflowGraphPage
      workspaceId={workspaceId}
      workflowKey={workflowKey}
      search={search}
      onSelect={(selection) => {
        // One parameter, never two: the three kinds are alternatives, and the
        // panel is about exactly one of them. Which parameter names which kind
        // is the feature's rule, not the router's (constitution 2.1).
        void navigate({ search: graphSelectionSearch(selection) });
      }}
      onClearSelection={() => {
        // The panel is closed, so both are meaningless — they leave the
        // address rather than lingering as a stale selection in shared links.
        void navigate({ search: {} });
      }}
    />
  );
}
